// Profile + change password (U5) — legacy users/ProfileComponent.vue and
// users/ChangePasswordComponent.vue.

import { FormEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { Lock1, Profile as ProfileIcon } from 'iconsax-reactjs';

import { apiErrorMessage, apiValidationErrors } from '../../api/client';
import { useAuth } from '../../components/AuthGate';
import { ErrorBlock, LoadingBlock } from '../../components/StateBlock';
import { t } from '../../i18n';
import { WorkspaceFieldGrid, WorkspaceSection } from '../../workspace/WorkspaceSection';
import { Profile, changePassword, fetchProfile, saveProfile } from './api';

type Toast = { msg: string; sev: 'success' | 'error' } | null;

const str = (value: unknown) => (value == null ? '' : String(value));

export function ProfilePage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'password' ? 'password' : 'profile';
  const [toast, setToast] = useState<Toast>(null);

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 960 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <ProfileIcon size={28} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t('shell.profile')}
        </Typography>
      </Stack>
      <Tabs value={tab} onChange={(_, value) => setParams(value === 'password' ? { tab: 'password' } : {}, { replace: true })}>
        <Tab value="profile" label={t('shell.profile')} icon={<ProfileIcon size={16} />} iconPosition="start" />
        <Tab value="password" label={t('shell.changePassword')} icon={<Lock1 size={16} />} iconPosition="start" />
      </Tabs>
      {tab === 'profile' ? <ProfileForm onToast={setToast} /> : <PasswordForm onToast={setToast} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {toast ? (
          <Alert severity={toast.sev} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Stack>
  );
}

function ProfileForm({ onToast }: { onToast: (toast: Toast) => void }) {
  const { refreshUser } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [settings, setSettings] = useState<Record<string, unknown>>({});
  const [avatar, setAvatar] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProfile()
      .then((data) => {
        setProfile(data);
        setName(data.name ?? '');
        setPhone(data.phone ?? '');
        setSettings({ collapsed: '0', fixed_layout: '0', play_sound: '0', theme: 'light', ...data.settings });
      })
      .catch((err) => setError(apiErrorMessage(err, t('common.unableToLoad'))));
  }, []);

  useEffect(() => {
    if (!avatar) return setPreview(null);
    const url = URL.createObjectURL(avatar);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [avatar]);

  if (error) return <ErrorBlock message={error} />;
  if (!profile) return <LoadingBlock />;

  const setSetting = (key: string, value: unknown) => setSettings((prev) => ({ ...prev, [key]: value }));
  const fieldError = (key: string) => errors[key]?.[0];

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setErrors({});
    try {
      const saved = await saveProfile({ name, phone, settings, avatar });
      setProfile((prev) => ({ ...(prev as Profile), ...saved }));
      setAvatar(null);
      onToast({ msg: t('shell.profile.saved'), sev: 'success' });
      void refreshUser();
    } catch (err) {
      setErrors(apiValidationErrors(err));
      onToast({ msg: apiErrorMessage(err, t('common.saveFailed')), sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2.5} component="form" onSubmit={submit}>
      <WorkspaceSection title={t('shell.profile.account')} description={t('shell.profile.accountDesc')}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
          <Avatar src={preview ?? profile.avatar ?? undefined} sx={{ width: 84, height: 84, fontSize: 28, bgcolor: 'primary.main' }}>
            {name.slice(0, 1).toUpperCase()}
          </Avatar>
          <Box>
            <Button component="label" variant="outlined" size="small">
              {t('shell.profile.chooseAvatar')}
              <input
                hidden
                type="file"
                accept="image/png,image/jpeg"
                data-testid="avatar-input"
                onChange={(e) => setAvatar(e.target.files?.[0] ?? null)}
              />
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              {t('shell.profile.avatarHint')}
            </Typography>
            {fieldError('avatar') ? (
              <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                {fieldError('avatar')}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        <WorkspaceFieldGrid>
          <TextField label={t('shell.profile.username')} value={profile.username ?? ''} InputProps={{ readOnly: true }} />
          <TextField label={t('shell.profile.email')} value={profile.email ?? ''} InputProps={{ readOnly: true }} />
          <TextField required label={t('shell.profile.name')} value={name} onChange={(e) => setName(e.target.value)} error={Boolean(fieldError('name'))} helperText={fieldError('name')} />
          <TextField required label={t('shell.profile.phone')} value={phone} onChange={(e) => setPhone(e.target.value)} error={Boolean(fieldError('phone'))} helperText={fieldError('phone')} />
          <TextField
            type="date"
            label={t('shell.profile.birthDate')}
            value={str(settings.birth_date).slice(0, 10)}
            onChange={(e) => setSetting('birth_date', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField multiline minRows={2} label={t('shell.profile.address')} value={str(settings.address)} onChange={(e) => setSetting('address', e.target.value)} />
        </WorkspaceFieldGrid>
      </WorkspaceSection>

      <WorkspaceSection title={t('shell.profile.preferences')} description={t('shell.profile.preferencesDesc')}>
        <Stack spacing={2}>
          <Box>
            <FormLabel sx={{ fontWeight: 700, fontSize: 13 }}>{t('shell.profile.sidebar')}</FormLabel>
            <RadioGroup row value={str(settings.collapsed) || '0'} onChange={(e) => setSetting('collapsed', e.target.value)}>
              <FormControlLabel value="0" control={<Radio />} label={t('shell.profile.expanded')} />
              <FormControlLabel value="1" control={<Radio />} label={t('shell.profile.collapsed')} />
            </RadioGroup>
          </Box>
          <Box>
            <FormLabel sx={{ fontWeight: 700, fontSize: 13 }}>{t('shell.profile.theme')}</FormLabel>
            <RadioGroup row value={str(settings.theme) || 'light'} onChange={(e) => setSetting('theme', e.target.value)}>
              <FormControlLabel value="dark" control={<Radio />} label={t('shell.theme.default')} />
              <FormControlLabel value="light" control={<Radio />} label={t('shell.theme.light')} />
            </RadioGroup>
          </Box>
          <FormControlLabel
            control={<Checkbox checked={str(settings.fixed_layout) === '1'} onChange={(e) => setSetting('fixed_layout', e.target.checked ? '1' : '0')} />}
            label={t('shell.profile.fixedLayout')}
          />
          <FormControlLabel
            control={<Checkbox checked={str(settings.play_sound) === '1'} onChange={(e) => setSetting('play_sound', e.target.checked ? '1' : '0')} />}
            label={t('shell.profile.playSound')}
          />
        </Stack>
      </WorkspaceSection>

      <Box>
        <Button type="submit" variant="contained" disabled={saving || !name || !phone}>
          {saving ? t('action.saving') : t('action.save')}
        </Button>
      </Box>
    </Stack>
  );
}

function PasswordForm({ onToast }: { onToast: (toast: Toast) => void }) {
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const mismatch = Boolean(confirmation) && password !== confirmation;
  const tooShort = Boolean(password) && password.length < 6;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (mismatch || tooShort) return;
    setSaving(true);
    setErrors({});
    setMessage(null);
    try {
      await changePassword({ current, password, password_confirmation: confirmation });
      setCurrent('');
      setPassword('');
      setConfirmation('');
      onToast({ msg: t('shell.password.changed'), sev: 'success' });
    } catch (err) {
      setErrors(apiValidationErrors(err));
      setMessage(apiErrorMessage(err, t('common.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (key: string) => {
    const value = errors[key]?.[0];
    return value && /^[a-z_]+$/.test(value) ? t(`api.${value}`) : value;
  };

  return (
    <Card component="form" onSubmit={submit} sx={{ maxWidth: 520 }}>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('shell.password.intro')}
          </Typography>
          {message ? (
            <Alert severity="error" data-testid="password-error">
              {message}
            </Alert>
          ) : null}
          <TextField
            required
            type="password"
            autoComplete="current-password"
            label={t('shell.password.current')}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            error={Boolean(fieldError('current'))}
            helperText={fieldError('current')}
          />
          <TextField
            required
            type="password"
            autoComplete="new-password"
            label={t('shell.password.new')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={tooShort || Boolean(fieldError('password'))}
            helperText={tooShort ? t('shell.password.minLength', { min: 6 }) : fieldError('password')}
          />
          <TextField
            required
            type="password"
            autoComplete="new-password"
            label={t('shell.password.confirm')}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            error={mismatch}
            helperText={mismatch ? t('shell.password.mismatch') : undefined}
          />
          <Box>
            <Button type="submit" variant="contained" disabled={saving || !current || !password || mismatch || tooShort}>
              {saving ? t('action.saving') : t('shell.changePassword')}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
